import { TextField, Typography } from '@mui/material';
import {
  FileUpload,
  FormSection,
  PhoneNumber,
  CustomRadioGroup,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories, Representation } from 'Enum';
import { useWatch, useFormContext } from 'react-hook-form';
import { generateError } from 'Regex';
import { useAppSelector, useDictionary } from 'Hooks';
import {
  validateLettersAndNumbersOnly,
  validateLettersOnly,
  validateStringNumbersOnly,
} from 'Utils/validateLettersAndNumbersOnly';
import { FormSectionWrapper } from 'Components/FormSectionWrapper';
import { styleTextField } from 'Theme';

export const FirstSection = () => {
  const representationOptionRelationIdName = 'representationOptionRelationId';

  const {
    register,
    formState: { errors },
  } = useFormContext();

  const [representationOptions] = useAppSelector(
    ({
      generalContent: {
        generalContent: { representationOptions },
      },
    }) => [representationOptions]
  );

  const representationOption = useWatch({
    name: representationOptionRelationIdName,
    defaultValue: 0,
  }) as number;

  const dictionary = useDictionary(Dictionary, DICTIONARY.REPRESENTATION);

  return (
    <FormSectionWrapper>
      <FormSection grouped id={dictionary.firstSectionTitle}>
        {representationOptions && (
          <CustomRadioGroup
            id={dictionary.firstSectionTitle}
            name={`${representationOptionRelationIdName}` as const}
            options={representationOptions.map(({ id, value }) => ({
              value: id.toString(),
              label: value,
            }))}
            infoText={dictionary.firstSectionInputTextHelper}
          />
        )}
        {representationOption == Representation.isLawyer && (
          <>
            <Typography sx={{ marginTop: 3, fontWeight: 'bold' }}>
              {dictionary.firstSectionThirdInputTextHelper}
            </Typography>
            <TextField
              role={
                Boolean(errors.lawyerSection?.firstName) ? 'alert' : undefined
              }
              inputProps={{
                maxLength: '20',
              }}
              error={Boolean(errors.lawyerSection?.firstName)}
              helperText={
                Boolean(errors.lawyerSection?.firstName?.message) &&
                generateError(
                  dictionary.firstSectionFirstInput,
                  errors.lawyerSection?.firstName?.message!
                )
              }
              {...register('lawyerSection.firstName')}
              label={dictionary.firstSectionFirstInput}
              id={dictionary.firstSectionFirstInput}
              onChange={(event: any) => validateLettersOnly(event)}
              sx={styleTextField}
            />
            <TextField
              role={
                Boolean(errors.lawyerSection?.lastName) ? 'alert' : undefined
              }
              inputProps={{
                maxLength: '30',
              }}
              error={Boolean(errors.lawyerSection?.lastName)}
              helperText={
                Boolean(errors.lawyerSection?.lastName?.message) &&
                generateError(
                  dictionary.firstSectionSecondInput,
                  errors.lawyerSection?.lastName?.message!
                )
              }
              {...register('lawyerSection.lastName')}
              label={dictionary.firstSectionSecondInput}
              id={dictionary.firstSectionSecondInput}
              onChange={(event: any) => validateLettersOnly(event)}
              sx={styleTextField}
            />
            <TextField
              role={
                Boolean(errors.lawyerSection?.address) ? 'alert' : undefined
              }
              inputProps={{
                maxLength: '50',
              }}
              {...register('lawyerSection.address')}
              error={Boolean(errors.lawyerSection?.address)}
              helperText={
                Boolean(errors.lawyerSection?.address?.message) &&
                generateError(
                  dictionary.firstSectionThirdInput,
                  errors.lawyerSection?.address?.message!
                )
              }
              label={dictionary.firstSectionThirdInput}
              id={dictionary.firstSectionThirdInput}
              onChange={(event: any) => validateLettersAndNumbersOnly(event)}
              sx={styleTextField}
            />

            <TextField
              role={
                Boolean(errors.lawyerSection?.phoneNum) ? 'alert' : undefined
              }
              inputProps={{
                maxLength: '30',
              }}
              error={Boolean(errors.lawyerSection?.phoneNum)}
              helperText={
                Boolean(errors.lawyerSection?.phoneNum?.message) &&
                generateError(
                  dictionary.phone,
                  errors.lawyerSection?.phoneNum?.message!
                )
              }
              {...register('lawyerSection.phoneNum')}
              label={dictionary.phone}
              id={dictionary.phone}
              onChange={(event: any) => validateStringNumbersOnly(event)}
              sx={styleTextField}
            />
            <Typography sx={{ marginTop: 2 }}>
              {dictionary.firstSectionFileUploadLabel}
            </Typography>

            <FileUpload
              fileCategoryId={FileCategories.LawyerPowerOfAttorneyAttachment}
              fileLabel={dictionary.firstSectionFileUploadText}
              ariaLabel={dictionary.firstSectionFileUploadLabel}
              dataRequired
            />
            <Typography sx={{ marginTop: 2 }}>
              {dictionary.firstSectionSecondFileUploadLabel}
            </Typography>
            <FileUpload
              fileLabel={dictionary.firstSectionSecondFileUploadText}
              fileCategoryId={FileCategories.LawyerRepresentationAttachment}
              ariaLabel={dictionary.firstSectionSecondFileUploadLabel}
              dataRequired
            />
          </>
        )}

        {representationOption == Representation.isAssociation && (
          <>
            <Typography sx={{ marginTop: 3, fontWeight: 'bold' }}>
              {dictionary.firstSectionAssociationTitle}
            </Typography>
            <TextField
              role={
                Boolean(errors.associationSection?.associationName)
                  ? 'alert'
                  : undefined
              }
              inputProps={{
                maxLength: '100',
              }}
              error={Boolean(errors.associationSection?.associationName)}
              helperText={
                Boolean(errors.associationSection?.associationName?.message) &&
                generateError(
                  dictionary.firstSectionAssociationName,
                  errors.associationSection?.associationName?.message!
                )
              }
              {...register('associationSection.associationName')}
              label={dictionary.firstSectionAssociationName}
              id={dictionary.firstSectionAssociationName}
              onChange={(event: any) => validateLettersOnly(event)}
              sx={styleTextField}
            />
            <TextField
              role={
                Boolean(errors.associationSection?.address)
                  ? 'alert'
                  : undefined
              }
              inputProps={{
                maxLength: '50',
              }}
              error={Boolean(errors.associationSection?.address)}
              helperText={
                Boolean(errors.associationSection?.address?.message) &&
                generateError(
                  dictionary.firstSectionAssociationAddress,
                  errors.associationSection?.address?.message!
                )
              }
              {...register('associationSection.address')}
              label={dictionary.firstSectionAssociationAddress}
              id={dictionary.firstSectionAssociationAddress}
              onChange={(event: any) => validateLettersAndNumbersOnly(event)}
              sx={styleTextField}
            />
            <PhoneNumber
              phoneNumberErrorMsg={errors.associationSection?.phone?.message}
              phoneNumberError={Boolean(
                errors.associationSection?.phone?.message
              )}
              phoneNumberPrefixFormName='associationSection.phonePrefixRelationId'
              phoneNumberFormName='associationSection.phone'
              phoneNumberLabel={dictionary.firstSectionAssociationPhone}
            />
            <TextField
              role={
                Boolean(errors.associationSection?.representativeName)
                  ? 'alert'
                  : undefined
              }
              inputProps={{
                maxLength: '50',
              }}
              error={Boolean(errors.associationSection?.representativeName)}
              helperText={
                Boolean(
                  errors.associationSection?.representativeName?.message
                ) &&
                generateError(
                  dictionary.firstSectionAssociationAgentName,
                  errors.associationSection?.representativeName?.message!
                )
              }
              {...register('associationSection.representativeName')}
              label={dictionary.firstSectionAssociationAgentName}
              id={dictionary.firstSectionAssociationAgentName}
              onChange={(event: any) => validateLettersOnly(event)}
              sx={styleTextField}
            />
            <TextField
              role={
                Boolean(errors.associationSection?.mail) ? 'alert' : undefined
              }
              inputProps={{
                maxLength: '50',
              }}
              error={Boolean(errors.associationSection?.mail)}
              helperText={
                Boolean(errors.associationSection?.mail?.message) &&
                generateError(
                  dictionary.firstSectionAssociationEmail,
                  errors.associationSection?.mail?.message!
                )
              }
              {...register('associationSection.mail')}
              label={dictionary.firstSectionAssociationEmail}
              id={dictionary.firstSectionAssociationEmail}
              sx={styleTextField}
            />

            <Typography>
              {
                Dictionary[DICTIONARY.REPRESENTATION]
                  .thirdSectionFileUploadLabel
              }
            </Typography>
            <FileUpload
              dataRequired
              fileLabel={
                Dictionary[DICTIONARY.REPRESENTATION].thirdSectionFileUploadText
              }
              fileCategoryId={
                FileCategories.AssociationRepresentationAttachment
              }
              ariaLabel={
                Dictionary[DICTIONARY.REPRESENTATION]
                  .thirdSectionFileUploadLabel
              }
            />
          </>
        )}
      </FormSection>
    </FormSectionWrapper>
  );
};
